import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BookOpen, Brain, User, Mail, Lock, Sparkles,
  Network, Bot, CheckCircle, ArrowRight, Play, Heart, Flame,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'

const LEARN_STEP_TITLES: Record<string, string> = {
  LS01: '변수 이해', LS02: '순서쌍·좌표평면', LS03: '함수 개념',
  LS04: '정비례 함수', LS05: '기울기 이해', LS06: 'x절편·y절편',
  LS07: '일차함수 식 세우기', LS08: '그래프 그리기', LS09: '두 점으로 식 구하기',
  LS10: '연립방정식 연결', LS11: '실생활 문제 모델링', LS12: '오답 패턴 분석',
}

const GRADE_OPTIONS = [
  { value: 1 as const, emoji: '🌱', label: '중1', desc: '수와 연산 · 문자와 식 기초' },
  { value: 2 as const, emoji: '🌿', label: '중2', desc: '일차함수 · 확률 · 도형' },
  { value: 3 as const, emoji: '🌳', label: '중3', desc: '이차함수 · 삼각비 · 통계' },
]

type Mode = 'welcome' | 'login' | 'register'

export default function AuthPage() {
  const navigate = useNavigate()
  const { session, isLoggedIn, register, login, initAuth } = useAuthStore()

  const [mode, setMode] = useState<Mode>('welcome')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [grade, setGrade] = useState<1|2|3>(2)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => { initAuth() }, [])
  useEffect(() => { if (isLoggedIn && session) navigate('/', { replace: true }) }, [isLoggedIn, session])

  const handleRegister = async () => {
    setError(''); setLoading(true)
    const result = await register({ name, email, password, grade })
    setLoading(false)
    if (result.ok) navigate('/', { replace: true })
    else setError(result.error ?? '오류가 발생했습니다.')
  }

  const handleLogin = async () => {
    setError(''); setLoading(true)
    const result = await login(email, password)
    setLoading(false)
    if (result.ok) navigate('/', { replace: true })
    else setError(result.error ?? '오류가 발생했습니다.')
  }

  const switchMode = (m: Mode) => { setMode(m); setError('') }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-accent-900 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">

        {/* 브랜드 헤더 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-primary-400 to-accent-500 rounded-2xl flex items-center justify-center shadow-xl">
              <span className="text-white text-xl font-black">f(x)</span>
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-black text-white">중학 수학 AI 튜터</h1>
              <p className="text-primary-300 text-sm">Operational Math Ontology · Agentic AI</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { icon: <Bot size={11} />, label: 'Agentic AI', color: 'bg-purple-500/30 text-purple-200 border-purple-500/40' },
              { icon: <Network size={11} />, label: 'MCP 연동', color: 'bg-indigo-500/30 text-indigo-200 border-indigo-500/40' },
              { icon: <Brain size={11} />, label: '오개념 자동 진단', color: 'bg-blue-500/30 text-blue-200 border-blue-500/40' },
              { icon: <Sparkles size={11} />, label: 'OWL 온톨로지', color: 'bg-teal-500/30 text-teal-200 border-teal-500/40' },
            ].map(({ icon, label, color }) => (
              <span key={label} className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${color}`}>
                {icon}{label}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* 왼쪽: AI 소개 패널 */}
          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Sparkles size={18} className="text-yellow-400" /> AI가 나를 위해 하는 일
              </h2>
              <div className="space-y-4">
                {[
                  { emoji: '🤖', title: 'Agentic AI 맞춤 지도', badge: 'Agentic AI', badgeColor: 'bg-purple-500/40 text-purple-200',
                    desc: '정답·오답 패턴을 실시간 분석해 다음 학습 경로를 자동으로 추천합니다.' },
                  { emoji: '🔌', title: 'MCP 도구 자동 호출', badge: 'MCP', badgeColor: 'bg-indigo-500/40 text-indigo-200',
                    desc: '온톨로지 DB, 규칙 엔진을 AI가 직접 호출해 개념 연결 관계와 추론 결과를 제공합니다.' },
                  { emoji: '🧠', title: '오개념 자동 감지', badge: 'Ontology', badgeColor: 'bg-teal-500/40 text-teal-200',
                    desc: '틀린 이유를 8가지 오개념 코드(M01~M08)로 분류하고 맞춤 해설을 제공합니다.' },
                  { emoji: '📈', title: '개인 학습 기록 저장', badge: 'PersonalDB', badgeColor: 'bg-blue-500/40 text-blue-200',
                    desc: '학습 단계, 문제풀이, TEST 결과를 저장해 언제든 이어서 학습할 수 있습니다.' },
                ].map(item => (
                  <div key={item.title} className="flex gap-3">
                    <div className="text-2xl flex-shrink-0 mt-0.5">{item.emoji}</div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-bold text-white">{item.title}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${item.badgeColor}`}>{item.badge}</span>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 커리큘럼 미리보기 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <BookOpen size={15} className="text-blue-300" /> 학습 커리큘럼
              </h3>
              <div className="space-y-2">
                {[
                  { grade: '중1', emoji: '🌱', areas: '수와 연산 · 문자와 식 · 기하 기초', units: 9 },
                  { grade: '중2', emoji: '🌿', areas: '일차함수 · 이차방정식 · 도형', units: 9, current: true },
                  { grade: '중3', emoji: '🌳', areas: '이차함수 · 삼각비 · 통계', units: 9 },
                ].map(row => (
                  <div key={row.grade} className={`flex items-center gap-3 rounded-xl p-2.5 ${row.current ? 'bg-primary-500/30 border border-primary-400/40' : 'bg-white/5'}`}>
                    <span className="text-lg">{row.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-white">{row.grade}</span>
                        {row.current && <span className="text-xs bg-primary-400/50 text-primary-200 px-1.5 py-0.5 rounded-full">현재 학습</span>}
                      </div>
                      <p className="text-xs text-slate-400 leading-tight">{row.areas}</p>
                    </div>
                    <span className="text-xs text-slate-400">{row.units}유닛</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 오른쪽: 인증 폼 */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

            {mode !== 'welcome' && (
              <div className="flex border-b border-slate-100">
                {(['login', 'register'] as Mode[]).map(m => (
                  <button key={m} onClick={() => switchMode(m)}
                    className={`flex-1 py-4 text-sm font-bold transition-colors ${
                      mode === m ? 'text-primary-700 border-b-2 border-primary-500 bg-primary-50' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {m === 'login' ? '🔑 로그인' : '✨ 회원가입'}
                  </button>
                ))}
              </div>
            )}

            <div className="p-8">

              {/* 환영 화면 */}
              {mode === 'welcome' && (
                <div className="text-center">
                  <div className="text-6xl mb-4">👋</div>
                  <h2 className="text-2xl font-black text-slate-800 mb-2">안녕하세요!</h2>
                  <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                    중학교 수학 AI 튜터에 오신 것을 환영합니다.<br />
                    개인 계정으로 학습 기록을 저장하고<br />
                    AI 맞춤 학습을 시작해보세요.
                  </p>

                  {/* ── 수포자 전용 입장 버튼 (최상단 강조) ── */}
                  <button
                    onClick={() => navigate('/onboarding')}
                    className="w-full mb-4 relative overflow-hidden rounded-2xl py-4 font-black text-sm text-white shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)' }}
                  >
                    <div className="absolute inset-0 opacity-20"
                      style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, white 0%, transparent 60%)' }} />
                    <div className="relative flex items-center justify-center gap-2">
                      <Heart size={16} className="fill-white" />
                      <span>수학을 포기한 적 있어요 (수포자 입장)</span>
                      <Flame size={16} />
                    </div>
                    <p className="relative text-xs text-white/70 mt-0.5">전용 온보딩으로 처음부터 다시 시작</p>
                  </button>

                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs text-slate-400">또는</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>

                  <div className="space-y-3">
                    <button onClick={() => switchMode('login')}
                      className="w-full bg-primary-600 text-white rounded-xl py-3.5 font-bold text-sm hover:bg-primary-700 transition-colors flex items-center justify-center gap-2">
                      <User size={16} /> 기존 계정으로 로그인
                    </button>
                    <button onClick={() => switchMode('register')}
                      className="w-full border-2 border-primary-200 text-primary-700 rounded-xl py-3.5 font-bold text-sm hover:bg-primary-50 transition-colors flex items-center justify-center gap-2">
                      <Sparkles size={16} /> 무료 회원가입
                    </button>
                  </div>
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <div className="flex items-center gap-2 justify-center text-xs text-slate-400 mb-2">
                      <CheckCircle size={12} className="text-emerald-400" /> 무료 · 광고 없음 · 데이터 안전 보관
                    </div>
                    <p className="text-xs text-slate-400">
                      가입하면 AI 튜터가 학습 기록을 분석해<br />
                      <strong className="text-slate-600">맞춤형 학습 경로</strong>를 추천해 드립니다.
                    </p>
                  </div>
                </div>
              )}

              {/* 로그인 폼 */}
              {mode === 'login' && (
                <div>
                  <h2 className="text-xl font-black text-slate-800 mb-1">다시 오셨군요!</h2>
                  <p className="text-sm text-slate-500 mb-6">로그인하면 AI가 마지막 학습 위치부터 안내해 드립니다.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-1.5">이메일</label>
                      <div className="relative">
                        <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                          placeholder="example@email.com" onKeyDown={e => e.key === 'Enter' && handleLogin()}
                          className="w-full pl-9 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-1.5">비밀번호</label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                          placeholder="비밀번호 입력" onKeyDown={e => e.key === 'Enter' && handleLogin()}
                          className="w-full pl-9 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400" />
                      </div>
                    </div>
                    {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-sm text-red-600">{error}</div>}
                    <button onClick={handleLogin} disabled={loading}
                      className="w-full bg-primary-600 text-white rounded-xl py-3.5 font-bold text-sm hover:bg-primary-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                      {loading ? <span className="animate-spin">⟳</span> : <>로그인 <ArrowRight size={15} /></>}
                    </button>
                  </div>
                  <p className="text-center text-xs text-slate-400 mt-5">
                    계정이 없으신가요?{' '}
                    <button onClick={() => switchMode('register')} className="text-primary-600 font-bold hover:underline">회원가입</button>
                  </p>
                </div>
              )}

              {/* 회원가입 폼 */}
              {mode === 'register' && (
                <div>
                  <h2 className="text-xl font-black text-slate-800 mb-1">AI 튜터와 함께 시작!</h2>
                  <p className="text-sm text-slate-500 mb-5">계정을 만들면 학습 기록이 저장되어 언제든 이어서 학습할 수 있습니다.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-1.5">이름 (닉네임)</label>
                      <div className="relative">
                        <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="예) 홍길동"
                          className="w-full pl-9 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-2">학년</label>
                      <div className="grid grid-cols-3 gap-2">
                        {GRADE_OPTIONS.map(opt => (
                          <button key={opt.value} onClick={() => setGrade(opt.value)}
                            className={`rounded-xl p-3 text-center border-2 transition-all ${
                              grade === opt.value
                                ? 'border-primary-500 bg-primary-50 text-primary-700'
                                : 'border-slate-200 text-slate-600 hover:border-primary-200'
                            }`}>
                            <div className="text-2xl mb-1">{opt.emoji}</div>
                            <div className="text-xs font-bold">{opt.label}</div>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{GRADE_OPTIONS.find(o => o.value === grade)?.desc}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-1.5">이메일</label>
                      <div className="relative">
                        <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="example@email.com"
                          className="w-full pl-9 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-1.5">비밀번호 (4자 이상)</label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                          placeholder="비밀번호 입력" onKeyDown={e => e.key === 'Enter' && handleRegister()}
                          className="w-full pl-9 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400" />
                      </div>
                    </div>
                    {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-sm text-red-600">{error}</div>}
                    <button onClick={handleRegister} disabled={loading}
                      className="w-full bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-xl py-3.5 font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2">
                      {loading ? <span className="animate-spin">⟳</span> : <><Sparkles size={15} /> AI 튜터와 학습 시작하기</>}
                    </button>
                  </div>
                  <p className="text-center text-xs text-slate-400 mt-5">
                    이미 계정이 있으신가요?{' '}
                    <button onClick={() => switchMode('login')} className="text-primary-600 font-bold hover:underline">로그인</button>
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
